<template>
    <div class="step-dc">
        <ConfirmModal />
        <h3>3. Import DartConnect</h3>

        <div class="form-group">
            <label>URL des résultats DartConnect</label>
            <input v-model="url" class="form-input" placeholder="https://tv.dartconnect.com/event/.../results" />
        </div>
        <button type="button" class="btn-secondary" :disabled="!url || loadingEvents" @click="loadEvents">
            {{ loadingEvents ? 'Chargement...' : 'Charger les épreuves' }}
        </button>

        <div v-if="dcEvents.length" class="mapping-table-wrapper">
            <h4>Mapping épreuve → catégorie / rôle</h4>
            <p class="hint">Laisser "Ignorer" pour une épreuve à ne pas importer.</p>
            <table class="mapping-table">
                <thead>
                    <tr>
                        <th>Épreuve DartConnect</th>
                        <th>Catégorie</th>
                        <th>Rôle</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="ev in dcEvents" :key="ev.dc_event_id">
                        <td>{{ ev.label }} <span class="hint">({{ ev.bracket_type }})</span></td>
                        <td>
                            <select v-model="mapping[ev.dc_event_id].category" class="form-input-sm">
                                <option value="">Ignorer</option>
                                <option value="M">Individuel Masculin</option>
                                <option value="F">Individuel Féminin</option>
                                <option value="V">Individuel Vétéran</option>
                                <option value="J">Individuel Junior</option>
                                <option value="DM">Double</option>
                                <option value="DF">Double Féminin</option>
                            </select>
                        </td>
                        <td>
                            <select v-model="mapping[ev.dc_event_id].role" class="form-input-sm" :disabled="!mapping[ev.dc_event_id].category">
                                <option value="RR">Round-Robin (poules)</option>
                                <option value="KO">Élimination directe</option>
                                <option value="KO_A">Élimination directe -- tableau A</option>
                                <option value="KO_B">Élimination directe -- tableau B (repêchage)</option>
                            </select>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="dcEvents.length" class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('back')">← Retour</button>
            <button type="button" class="btn-submit" :disabled="!activeMappingCount || running" @click="startImport">
                {{ running ? 'Import en cours...' : `Lancer l'import (${activeMappingCount} épreuve${activeMappingCount > 1 ? 's' : ''})` }}
            </button>
        </div>

        <div v-if="jobStatus === 'blocked'" class="blocked-block">
            <p class="issue-banner">
                ⚠️ Import bloqué -- {{ blockedIssueCount }} ligne(s) en erreur. Corrigez le prérequis côté source
                (inscription, barème) puis relancez.
            </p>
            <table class="rows-table">
                <thead>
                    <tr><th>Catégorie</th><th>Joueur</th><th>Issue</th></tr>
                </thead>
                <tbody>
                    <tr v-for="(row, idx) in blockedRows.filter(r => r.issue)" :key="idx">
                        <td>{{ row.category }}</td>
                        <td>{{ row.player_name_raw }}</td>
                        <td class="issue-cell">{{ row.issue }}</td>
                    </tr>
                </tbody>
            </table>
            <div class="blocked-actions">
                <button
                    v-if="unregisteredCount"
                    type="button"
                    class="btn-secondary"
                    :disabled="registering"
                    @click="onQuickRegister"
                >
                    {{ registering ? 'Inscription...' : `Ajouter les ${unregisteredCount} inscription(s) manquante(s)` }}
                </button>
                <button type="button" class="btn-submit" :disabled="running" @click="startImport">
                    Relancer l'import
                </button>
            </div>
        </div>

        <div v-if="jobStatus === 'done' || jobStatus === 'done_with_errors'" class="ok-banner">
            ✅ Import terminé -- {{ jobResult?.rows_imported }} ligne(s) importée(s).
            <span v-if="Object.keys(jobErrors).length">
                (erreurs sur : {{ Object.keys(jobErrors).join(', ') }})
            </span>
        </div>
        <div v-if="jobStatus === 'error'" class="issue-banner">
            ❌ Erreur d'import : {{ jobErrorMessage }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { handleError } from '@/functions/utils'
import ConfirmModal from '@/components/ConfirmModal.vue'
import { useConfirm } from '@/composables/useConfirm'
import type { EnrichedRow, WizardParams } from './types'

const props = defineProps<{ params: WizardParams }>()
const emit = defineEmits<{ next: [result: { ranking_tournament_id: number; rows_imported: number }]; back: [] }>()

const { confirm } = useConfirm()

interface DcEvent {
    dc_event_id: number
    label: string
    bracket_type: string
    event_format: string
}

const url = ref('')
const loadingEvents = ref(false)
const dcEvents = ref<DcEvent[]>([])
const mapping = ref<Record<number, { category: string; role: string }>>({})

const running = ref(false)
const registering = ref(false)
const jobStatus = ref<string | null>(null)
const jobResult = ref<{ ranking_tournament_id: number; rows_imported: number } | null>(null)
const jobErrors = ref<Record<string, string>>({})
const jobErrorMessage = ref('')
const blockedRows = ref<EnrichedRow[]>([])

const activeMappingCount = computed(() => Object.values(mapping.value).filter((m) => m.category).length)
// blockedRows contient TOUTES les lignes traitées (y compris celles sans souci, cf. le
// "rows": [asdict(r) for r in enriched] renvoyé par le backend quand le job est "blocked")
// -- seules celles avec une `issue` bloquent réellement l'import.
const blockedIssueCount = computed(() => blockedRows.value.filter((r) => r.issue).length)
// cf. StepPreview.vue -- même détection, même endpoint. Le scraping DartConnect n'étant
// pas incrémental, on ne peut pas corriger une ligne "in place" ici (contrairement à
// StepPreview) -- seul l'ajout en lot des inscriptions manquantes est possible avant de
// relancer tout l'import.
const unregisteredCount = computed(
    () => blockedRows.value.filter((r) => r.issue?.includes('non inscrit')).length
)

async function onQuickRegister() {
    const flagged = blockedRows.value.filter((r) => r.issue?.includes('non inscrit'))
    if (!flagged.length) return

    const accepted = await confirm(
        `Ajouter ${flagged.length} inscription(s) pour "${props.params.name}" à partir des noms scrapés ? ` +
        'À utiliser seulement si ce sont de vrais participants (tournoi sans inscriptions natives).'
    )
    if (!accepted) return

    registering.value = true
    try {
        await dartsHubApi.post('/leaderboard/import/quick-register', {
            event_id: props.params.eventId,
            scope_level: props.params.scopeLevel,
            scope_id: props.params.scopeId,
            rows: flagged.map((r) => ({ category: r.category, player_name_raw: r.player_name_raw })),
        })
        toast.success(`${flagged.length} inscription(s) ajoutée(s).`)
    } catch (err) {
        handleError(err, 'ajout des inscriptions manquantes')
    } finally {
        registering.value = false
    }
}

async function loadEvents() {
    loadingEvents.value = true
    try {
        const { data } = await dartsHubApi.get('/leaderboard/dartconnect/events', { params: { url: url.value } })
        dcEvents.value = data
        mapping.value = Object.fromEntries(data.map((e: DcEvent) => [e.dc_event_id, { category: '', role: 'KO' }]))
    } catch (err) {
        handleError(err, 'chargement des épreuves DartConnect')
    } finally {
        loadingEvents.value = false
    }
}

async function startImport() {
    if (jobStatus.value !== 'blocked') {
        const accepted = await confirm(
            `Lancer l'import DartConnect pour "${props.params.name}" (${activeMappingCount.value} épreuve(s)) ?`
        )
        if (!accepted) return
    }

    running.value = true
    jobStatus.value = null
    blockedRows.value = []
    try {
        const body = {
            source_type_code: props.params.sourceTypeCode,
            season_id: props.params.seasonId,
            scope_level: props.params.scopeLevel,
            scope_id: props.params.scopeId,
            event_id: props.params.eventId,
            tournament_date: props.params.tournamentDate,
            rows: [],
            name: props.params.name,
            place: props.params.place,
            source: 'dartconnect',
            tournament_url: url.value,
            mapping: Object.entries(mapping.value)
                .filter(([, m]) => m.category)
                .map(([dc_event_id, m]) => ({ dc_event_id: Number(dc_event_id), category: m.category, role: m.role })),
        }
        const { data: started } = await dartsHubApi.post('/leaderboard/dartconnect/import', body)
        await pollStatus(started.job_id)
    } catch (err) {
        handleError(err, 'lancement de l\'import DartConnect')
        running.value = false
    }
}

async function pollStatus(jobId: string) {
    // Pas de WebSocket dispo ici -- polling simple, le job tourne en tâche de fond côté
    // serveur (scraping DartConnect, potentiellement plusieurs secondes par épreuve).
    while (true) {
        await new Promise((r) => setTimeout(r, 1500))
        const { data } = await dartsHubApi.get(`/leaderboard/dartconnect/import/status/${jobId}`)
        if (data.status === 'pending' || data.status === 'running') continue

        jobStatus.value = data.status
        running.value = false
        if (data.status === 'blocked') {
            blockedRows.value = data.rows ?? []
        } else if (data.status === 'done' || data.status === 'done_with_errors') {
            jobResult.value = data.result
            jobErrors.value = data.errors ?? {}
            toast.success('Import DartConnect terminé.')
            emit('next', data.result)
        } else if (data.status === 'error') {
            jobErrorMessage.value = data.error ?? 'erreur inconnue'
        }
        return
    }
}
</script>

<style scoped>
.step-dc h3 {
    margin-bottom: 1rem;
}

.form-group {
    margin-bottom: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    max-width: 480px;
}

.form-group label {
    font-weight: 600;
    font-size: 0.9rem;
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
}

.mapping-table-wrapper {
    margin-top: 1.5rem;
}

.mapping-table,
.rows-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
    margin-top: 0.5rem;
}

.mapping-table th,
.mapping-table td,
.rows-table th,
.rows-table td {
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid var(--color-light-shadow, #eee);
    text-align: left;
}

.form-input-sm {
    padding: 0.3rem 0.4rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: 4px;
    font-size: 0.85rem;
}

.issue-banner {
    color: var(--color-danger, #d9534f);
    font-weight: 600;
    margin: 1rem 0;
}

.ok-banner {
    color: var(--color-success, #38c793);
    font-weight: 600;
    margin: 1rem 0;
}

.issue-cell {
    color: var(--color-danger, #d9534f);
}

.blocked-block {
    margin-top: 1rem;
}

.blocked-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
}

.form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: var(--radius, 8px);
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--color-accent, #1a3a5c);
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}
</style>
